import { useCallback, useEffect } from 'react'
import {
	getGetMeQueryKey,
	useUpdateMe,
} from '@cocoonspace/sdk-js/domains/profile/profile'
import {
	deleteGoogleAuth,
	deleteOutlookAuth,
	getGetGoogleStatusQueryKey,
	getGetOutlookStatusQueryKey,
	getGoogleAuthUrl,
	getOutlookAuthUrl,
} from '@cocoonspace/sdk-js/v2/domains/auth/auth'
import { getListIntegrationsQueryKey } from '@cocoonspace/sdk-js/v2/domains/me-integrations/me-integrations'
import { getListEventTypeCalendarsQueryKey } from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import { sleep } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import {
	type QueryKey,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

export type Provider = keyof typeof PROVIDER_CONFIG

export const PROVIDER_CONFIG = {
	google: {
		getAuthUrl: getGoogleAuthUrl,
		success: 'GOOGLE_OAUTH_SUCCESS',
		error: 'GOOGLE_OAUTH_ERROR',
	},
	outlook: {
		getAuthUrl: getOutlookAuthUrl,
		success: 'OUTLOOK_OAUTH_SUCCESS',
		error: 'OUTLOOK_OAUTH_ERROR',
	},
} as const

const useRemoveIntegration = (provider: Provider) => {
	return useMutation({
		mutationKey: ['remove-integration', provider],
		mutationFn: () => {
			switch (provider) {
				case 'google':
					return deleteGoogleAuth()
				case 'outlook':
					return deleteOutlookAuth()
			}
		},
	})
}

export const useConnectIntegration = ({
	provider,
	onConnectSuccess,
	onConnectError,
}: {
	provider: Provider
	onConnectSuccess?: () => void
	onConnectError?: () => void
}) => {
	const { t } = useLingui()
	const queryClient = useQueryClient()
	const userUpdate = useUpdateMe()
	const removeIntegrationMutation = useRemoveIntegration(provider)

	const openAuthPopup = useCallback(async () => {
		const redirectUrl = `/auth/integrations/redirect/${provider}`
		const res = await PROVIDER_CONFIG[provider].getAuthUrl({ redirectUrl })

		window.open(res.authorizationUrl, 'oauth-popup', 'width=500,height=600')
	}, [provider])

	const invalidateQueries = useCallback(() => {
		let queryKey: QueryKey

		queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() })
		queryClient.invalidateQueries({ queryKey: getListIntegrationsQueryKey() })
		queryClient.invalidateQueries({
			queryKey: getListEventTypeCalendarsQueryKey(),
		})

		switch (provider) {
			case 'google':
				queryKey = getGetGoogleStatusQueryKey()
				break
			case 'outlook':
				queryKey = getGetOutlookStatusQueryKey()
				break
		}

		queryClient.invalidateQueries({ queryKey })
	}, [queryClient, provider])

	const removeIntegration = useCallback(() => {
		const query = removeIntegrationMutation.mutateAsync().then(async () => {
			await sleep(1000)

			// Disable ics attachments
			await userUpdate.mutateAsync({ data: { ics_attachments: false } })

			invalidateQueries()
		})

		toast.promise(query, {
			loading: t`Désactivation en cours`,
			success: t`Intégration désactivée`,
			error: t`Erreur lors de la désactivation`,
		})
	}, [removeIntegrationMutation, t, userUpdate, invalidateQueries])

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			const isSameOrigin = event.origin === window.location.origin
			if (!isSameOrigin) return

			const msgs = PROVIDER_CONFIG[provider] ?? {}
			const isSuccessMsg = event.data?.type === msgs.success
			const isErrorMsg = event.data?.type === msgs.error

			if (isErrorMsg) onConnectError?.()

			if (isSuccessMsg) {
				onConnectSuccess?.()
				invalidateQueries()
			}
		}

		window.addEventListener('message', handleMessage)

		return () => window.removeEventListener('message', handleMessage)
	}, [onConnectSuccess, onConnectError, provider, invalidateQueries])

	return {
		openAuthPopup,
		removeIntegration,
		isPending: removeIntegrationMutation.isPending,
	}
}
